import { useCallback, useRef, useState } from 'react';

import { Edit02Icon, SentIcon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import cn from 'classnames';
import { EMAIL_TEMPLATE_TYPES } from 'constants/email-config';
import { useIdVerificationSummary } from 'hooks/id-verification-summary';
import { Tab, Tabs } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

import { AnimatedModal } from 'components/AnimatedModal/AnimatedModal';
import { EmailTemplateStep } from 'components/EmailTemplateStep/EmailTemplateStep';
import { Button } from 'components/FormComponents';

import styles from './Candidate.module.scss';
import { CandidateCustomFields } from './tabs/CandidateCustomFields/CandidateCustomFields';
import { CandidateIdVerification } from './tabs/CandidateIdVerification/CandidateIdVerification';
import { CandidateInformation } from './tabs/CandidateInformation/CandidateInformation';

export const Candidate = ({
  candidate,
  company,
  user,
  teams = [],
  companyUsers,
  companyChecks = [],
  referees = [],
  customFields,
  brand,
  hideBrand,
  billingEntityOptionSet = null,
  handleCandidateUpdate = async () => {},
  sendRequestEmail = async () => {},
}) => {
  const { t } = useTranslation();
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [showUpdateAlert, setShowUpdateAlert] = useState(false);
  const [activeTabKey, setActiveTabKey] = useState('information');
  const informationFormRef = useRef(null);
  const customFieldsFormRef = useRef(null);

  const {
    isLoading: isIdvLoading,
    idvStatus,
    idvStatusKey,
    faceSimilarity,
    selfie,
    idDoc,
    idDocInfo,
  } = useIdVerificationSummary(candidate?.id ?? null);

  const idvSummary = { idvStatus, idvStatusKey, faceSimilarity, selfie, idDoc, idDocInfo };

  const showIdvTab = idvStatus === 'verified';

  const onUpdate = async data => {
    await handleCandidateUpdate(data);
    setShowUpdateAlert(!candidate.completed_at && !candidate.canceled);
  };

  const handleSendRequest = templateId => {
    setShowRequestModal(false);
    sendRequestEmail(templateId);
  };

  const getBrandCandidateEmailTemplateId = useCallback(() => {
    return brand?.email_active ? (brand?.email_template_links?.candidate_request ?? null) : null;
  }, [brand]);

  return (
    <div
      className={cn(
        'd-flex',
        'border border-neutral-4 rounded-3xl shadow-soft overflow-hidden bg-neutral-1',
      )}>
      <AnimatedModal
        visible={showRequestModal}
        onClose={() => setShowRequestModal(false)}
        title={t('candidate.sendRequestModal.title')}>
        <EmailTemplateStep
          brand={brand}
          category="candidate"
          submitLabel={t('candidate.sendRequestModal.sendButton')}
          templateIdInBrandDefault={getBrandCandidateEmailTemplateId()}
          type={EMAIL_TEMPLATE_TYPES.CANDIDATE_REQUEST}
          onSubmit={handleSendRequest}
        />
      </AnimatedModal>

      <div className="u-width-100">
        <div className="flex flex-row gap-2 justify-between items-center p-8">
          <div className="flex flex-col gap-1">
            <div className="text-text-primary text-[18px] font-semibold">Candidate Information</div>
            <div className="text-text-tertiary text-[14px] font-light">
              This information can be updated at any time
            </div>
          </div>
          <div className="flex flex-row gap-8">
            {!candidate.completed_at && !candidate.canceled && (
              <Button
                type="secondary"
                disabled={
                  candidate.partner_application_id && !candidate.partner_application_started
                }
                onClick={() => setShowRequestModal(true)}>
                <HugeiconsIcon icon={SentIcon} size="20" strokeWidth="1.5" className="mr-2" /> Send
                Request
              </Button>
            )}
            <Button
              onClick={() => {
                if (activeTabKey === 'information') informationFormRef.current?.submit?.();
                else if (activeTabKey === 'custom_fields') customFieldsFormRef.current?.submit?.();
              }}>
              <HugeiconsIcon icon={Edit02Icon} size="20" strokeWidth="1.5" className="mr-2" />{' '}
              Update Candidate
            </Button>
          </div>
        </div>

        <Tabs
          activeKey={activeTabKey}
          onSelect={k => setActiveTabKey(k ?? 'information')}
          className={cn(styles.tabs, 'tabs', 'padding-small', 'no-padding', 'bg-neutral-2')}
          mountOnEnter
          unmountOnExit>
          <Tab eventKey="information" title="Details">
            <div className="bg-neutral-2">
              <div className="w-full">
                <CandidateInformation
                  ref={informationFormRef}
                  candidate={candidate}
                  brand={brand}
                  company={company}
                  teams={teams}
                  companyUsers={companyUsers}
                  companyChecks={companyChecks}
                  referees={referees}
                  onUpdate={onUpdate}
                  hideBrand={hideBrand}
                  billingEntityOptionSet={billingEntityOptionSet}
                />
              </div>
            </div>
          </Tab>
          {customFields?.length > 0 && (
            <Tab eventKey="custom_fields" title="Custom Fields">
              <div className="bg-neutral-2">
                <div className="w-1/3">
                  <CandidateCustomFields
                    ref={customFieldsFormRef}
                    candidate={candidate}
                    customFields={customFields}
                    onUpdate={onUpdate}
                  />
                </div>
              </div>
            </Tab>
          )}
          {showIdvTab && (
            <Tab eventKey="id_verification" title="ID Verification">
              <div className="bg-neutral-2">
                <div className="w-1/3">
                  <CandidateIdVerification idvSummary={idvSummary} isLoading={isIdvLoading} />
                </div>
              </div>
            </Tab>
          )}
        </Tabs>
      </div>
    </div>
  );
};
